import React from "react";
import Nav from "@/components/Nav";
import Footer from "@/components/Footer";
import { Phone, MicrophoneSlash, CarProfile, ChecksIcon, Record } from "@phosphor-icons/react";

const STEPS = [
  { n: "01", icon: <Phone size={28} weight="thin" />, t: "Call the local line", s: "One number for the island you are on. Save it before you fly." },
  { n: "02", icon: <Record size={28} weight="thin" />, t: "Brief disclosure", s: "A short message notifies you the call may be recorded for service quality." },
  { n: "03", icon: <MicrophoneSlash size={28} weight="thin" />, t: "Say your pickup", s: "State your time and location, or press one to be connected immediately." },
  { n: "04", icon: <CarProfile size={28} weight="thin" />, t: "Live routing", s: "We connect you in real time to the currently active licensed operator." },
  { n: "05", icon: <ChecksIcon size={28} weight="thin" />, t: "Confirmed on the line", s: "Your driver confirms the pickup with you directly before you hang up." },
];

export default function HowItWorks() {
  return (
    <div className="bg-cream min-h-screen">
      <Nav />
      <section className="max-w-7xl mx-auto px-6 sm:px-10 pt-20 pb-8">
        <div className="text-[11px] uppercase tracking-[0.35em] text-teal mb-6">— How it works</div>
        <h1 className="font-display text-5xl sm:text-7xl text-navy leading-[0.95] max-w-3xl">
          Booking a private transfer, <em className="text-gold not-italic">without the app.</em>
        </h1>
        <p className="mt-6 text-navy/70 max-w-2xl leading-relaxed text-lg">
          LocalRida is deliberately simple. There is no account, no download, no surge pricing.
          The single call below is the whole product.
        </p>
      </section>

      <section className="max-w-7xl mx-auto px-6 sm:px-10 py-16">
        <div className="grid md:grid-cols-5 gap-0 border-t border-cream2">
          {STEPS.map((s, i) => (
            <div
              key={i}
              className={`p-8 border-b border-cream2 md:border-b-0 ${i < STEPS.length - 1 ? "md:border-r" : ""} md:border-cream2 hover:bg-cream2/40 transition-colors`}
              data-testid={`how-step-${i}`}
            >
              <div className="text-teal">{s.icon}</div>
              <div className="mt-6 text-[10px] uppercase tracking-[0.28em] text-gold">{s.n}</div>
              <div className="mt-2 font-display text-2xl text-navy leading-tight">{s.t}</div>
              <p className="mt-3 text-navy/60 text-sm leading-relaxed">{s.s}</p>
            </div>
          ))}
        </div>
      </section>

      {/* Compliance strip */}
      <section className="bg-navy-deep text-offwhite">
        <div className="max-w-7xl mx-auto px-6 sm:px-10 py-20 grid md:grid-cols-2 gap-14">
          <div>
            <div className="text-[10px] uppercase tracking-[0.28em] text-gold-bright mb-4">Transparency</div>
            <h2 className="font-display text-4xl leading-tight">Recording · Routing · Privacy.</h2>
          </div>
          <div className="space-y-8 text-offwhite/75 leading-relaxed">
            <p>
              Every incoming call plays a brief disclosure before any information is captured.
              Recordings help us maintain the standard of service you booked and are stored securely.
            </p>
            <p>
              Calls route live to whichever licensed operator is currently on duty for that island.
              Operators are added only after they have formally consented to receive our leads.
            </p>
            <p>
              Your phone number is never shown publicly or shared beyond the operator connecting your ride.
            </p>
          </div>
        </div>
      </section>

      {/* FAQ — SEO-targeted */}
      <section className="max-w-4xl mx-auto px-6 sm:px-10 py-20" data-testid="faq-section">
        <div className="text-[11px] uppercase tracking-[0.35em] text-teal mb-6">— Frequently asked</div>
        <h2 className="font-display text-4xl sm:text-5xl text-navy leading-tight mb-10">
          Common questions from arriving travelers.
        </h2>
        <div className="space-y-8">
          <div data-testid="faq-airbnb">
            <div className="font-display text-2xl text-navy">Do you pick up from Airbnbs and vacation rentals?</div>
            <p className="mt-3 text-navy/70 leading-relaxed">
              Yes. Villas, condos, private homes, Airbnbs — any address in Aruba works. There's no lobby to
              meet at, so your driver will call or WhatsApp on arrival and follow the exact instructions you
              give (gate code, parking spot, unit number).
            </p>
          </div>
          <div data-testid="faq-san-nicolas">
            <div className="font-display text-2xl text-navy">
              Heading to San Nicolas for the murals or Art Fair?
            </div>
            <p className="mt-3 text-navy/70 leading-relaxed">
              We'll get you there and back. San Nicolas — Aruba's cultural capital, about 30 minutes south of
              Oranjestad — is roughly a half-hour ride from most Palm Beach and Eagle Beach hotels.
              Day-trips for the mural walk, the galleries, and the annual Aruba Art Fair (September 11–13)
              are common bookings; you can either book a one-way or a return with a set pickup time.
            </p>
          </div>
          <div data-testid="faq-service-area">
            <div className="font-display text-2xl text-navy">Where on the island do you go?</div>
            <p className="mt-3 text-navy/70 leading-relaxed">
              Anywhere in Aruba. Hotels, resorts, and vacation rentals; airport and cruise port; Oranjestad
              downtown; Palm Beach, Eagle Beach, Malmok; San Nicolas art district; Arikok National Park;
              Arashi Beach and California Lighthouse. Not limited to the tourist strip.
            </p>
          </div>
          <div data-testid="faq-scheduling">
            <div className="font-display text-2xl text-navy">Can I book ahead for a future date?</div>
            <p className="mt-3 text-navy/70 leading-relaxed">
              Yes. Use the form on the homepage or /aruba to reserve a pickup for any future date and time,
              or select "As soon as possible" if you need a ride now. Calls and WhatsApp messages are used
              the same way — mention "now" or a future date and we'll handle it.
            </p>
          </div>
        </div>
      </section>

      <Footer />
    </div>
  );
}
